import { state } from "./state.js";
import { getSetting } from "./settings.js";
import { sessionHandlers } from "./sessions/index.js";
import { setInfoText, switchTab, urlValueForTab, setParameterInCurrentURL } from "./dom/layout.js";
import { arraysEqual } from "./util/format.js";

function currentHandlers() {
  return sessionHandlers[state.currentSessionType];
}

function setNewClass(newClasses) {
  const difference = newClasses.filter((cls) => !state.currentClasses.includes(cls));
  if (state.selectNewClass && difference.length === 1) {
    state.newClass = difference[0];
  } else if (getSetting("showRankingAll")) {
    state.newClass = "ALL";
  } else {
    state.newClass = newClasses[0];
  }
}

function createEvent() {
  const eventDiv = document.getElementById("headerData");
  eventDiv.innerHTML = "";
  currentHandlers()?.createEvent(eventDiv);
}

function setEvent(data) {
  currentHandlers()?.setEvent(data);
}

function createSessionData() {
  const sData = document.getElementById("sessionData");
  sData.innerHTML = "";
  currentHandlers()?.createSessionData?.(sData);
}

function createRankingDiv(data) {
  const rankingdiv = document.getElementById("Ranking");
  rankingdiv.innerHTML = "";
  currentHandlers()?.createRanking(rankingdiv, data);
  restorePendingUrlTab();
  syncSelectedTabToUrl();
}

// One-shot: restores the exact tab (e.g. "G0V1", not just class "G0") requested
// via the "?class=" URL param, once the first set of tabs exists after load.
// If it doesn't match any tab currently being built, the natural default
// (set by createRanking() above) is left in place.
function restorePendingUrlTab() {
  if (state.pendingUrlTab === null) return;
  const wanted = state.pendingUrlTab;
  state.pendingUrlTab = null;
  if (state.tableIdentifires.some((t) => t.Identifire === wanted)) {
    switchTab(wanted);
  }
}

// Keeps "?class=" matching whatever tab actually ended up focused - e.g. when
// a previously selected class no longer exists in a newly loaded event and
// the view falls back to the default, the stale param is cleared too.
function syncSelectedTabToUrl() {
  if (!state.currentButton) return;
  const identifire = state.currentButton.id.slice(3); // strip the "btn" prefix
  setParameterInCurrentURL("class", urlValueForTab(identifire));
}

function createRankingHead() {
  currentHandlers()?.createRankingHead();
}

export function updateRanking(data) {
  currentHandlers()?.updateRanking(data);
}

export function setSession(data) {
  // The server does not always send DisplayClass/ShowGroup/ShowMake/NoSectors
  // (seen on the live server) - fall back to sensible defaults instead of
  // letting these silently become undefined (which previously hid the class
  // column even for multi-class events). For ShowGroup/ShowMake, a
  // config.json "displayGroup"/"displayMake" override is honored when the
  // server doesn't send them.
  const displayClasses = data.DisplayClass ?? data.Classes.length > 1;
  const displayGroup = data.ShowGroup ?? getSetting("displayGroup") ?? false;
  const displayMake = data.ShowMake ?? getSetting("displayMake") ?? false;
  const noSectors = data.NoSectors ?? 0;

  if (state.currentSessionType !== data.SessionType) {
    setNewClass(data.Classes);
    state.currentSessionType = data.SessionType;
    state.displayClasses = displayClasses;
    state.currentSlalomMode = data.SlalomMode;
    state.currentClasses = data.Classes;
    state.displayGuest = data.ShowGuest;
    state.displayGroup = displayGroup;
    state.displayMake = displayMake;
    state.selectNewClass = data.SelectNewClass;
    state.noSectors = noSectors;
    createEvent();
    createSessionData();
    createRankingDiv(data);
    createRankingHead();
  } else if (state.displayClasses !== displayClasses) {
    state.newClass = "ALL";
    state.displayClasses = displayClasses;
    state.currentClasses = data.Classes;
    createRankingDiv(data);
    createRankingHead();
  } else if (state.currentSlalomMode !== data.SlalomMode) {
    state.newClass = "ALL";
    state.currentSlalomMode = data.SlalomMode;
    state.currentClasses = data.Classes;
    createRankingDiv(data);
    createRankingHead();
  } else if (!arraysEqual(state.currentClasses, data.Classes)) {
    setNewClass(data.Classes);
    state.currentClasses = data.Classes;
    createRankingDiv(data);
    createRankingHead();
  } else if (state.displayGuest !== data.ShowGuest) {
    state.newClass = "ALL";
    state.displayGuest = data.ShowGuest;
    createRankingDiv(data);
    createRankingHead();
  } else if (state.displayGroup !== displayGroup) {
    state.newClass = "ALL";
    state.displayGroup = displayGroup;
    createRankingDiv(data);
    createRankingHead();
  } else if (state.displayMake !== displayMake) {
    state.newClass = "ALL";
    state.displayMake = displayMake;
    createRankingDiv(data);
    createRankingHead();
  } else if (state.noSectors !== noSectors) {
    state.noSectors = noSectors;
    createRankingDiv(data);
    createRankingHead();
  }

  setEvent(data);
  setTime(data);
  currentHandlers()?.setSessionData?.(data);
}

export function clearAll() {
  state.currentSessionType = -1;

  const eventDiv = document.getElementById("headerData");
  eventDiv.innerHTML = "";
  const label = document.createElement("p");
  label.textContent = "Veranstaltung: ";
  eventDiv.append(label);
  const name = document.createElement("p");
  name.id = "eventName";
  name.textContent = "Keine Veranstaltung aktiv";
  eventDiv.append(name);

  document.getElementById("sessionData").innerHTML = "";
  setInfoText("");

  state.displayRaceTime = false;
  document.getElementById("timeData").innerHTML = "";
  document.getElementById("Ranking").innerHTML = "";
}

export function setTime(data) {
  if (data.hasOwnProperty("TotalRaceTime") && data.TotalRaceTime !== "0") {
    if (!document.getElementById("currentRaceTime")) {
      state.displayRaceTime = true;
      const tData = document.getElementById("timeData");
      const current = document.createElement("p");
      current.id = "currentRaceTime";
      current.textContent = "0:00";
      tData.append(current);
      const separator = document.createElement("p");
      separator.textContent = "/";
      tData.append(separator);
      const total = document.createElement("p");
      total.id = "totalRaceTime";
      total.textContent = "0:00";
      tData.append(total);
    }
    document.getElementById("totalRaceTime").innerHTML = data.TotalRaceTime;
  } else {
    state.displayRaceTime = false;
    document.getElementById("timeData").innerHTML = "";
  }
}

export function setRemainingTime(time) {
  if (state.displayRaceTime) {
    document.getElementById("currentRaceTime").innerHTML = time;
  }
}
